import moment from 'moment';

export interface ProgrammingStageTimestamps {
    expectedOriginDepartureAt?: string;
    expectedDestinationArrivalAt?: string;
    expectedDestinationDepartureAt?: string;
    expectedReturnArrivalAt?: string;
    actualOriginEnteredAt?: string;
    actualOriginExitedAt?: string;
    actualDestinationEnteredAt?: string;
    actualDestinationExitedAt?: string;
    actualReturnEnteredAt?: string;
    actualReturnExitedAt?: string;
    expectedDepartureTime?: string;
    expectedArrivalTime?: string;
    isGoAndReturn?: boolean;
    isReturn?: boolean;
}

export interface TimelineStage {
    key: string;
    label: string;
    expected?: string;
    actual?: string;
}

/**
 * Expected* legacy: relógio de parede SP serializado com sufixo Z (sem conversão de fuso na exibição).
 * Ver docs/features/torre-programming-datetime-convention.md
 */
const SAO_PAULO_OFFSET = '-03:00';

const SAO_PAULO_UTC_OFFSET_MINUTES = -180;

/** Planejado / datetime-local: mantém componentes de relógio (ida e volta com o form). */
export function formatProgrammingDateTime(value?: string | Date | null): string {
    if (!value) return '—';
    return moment.parseZone(value).utcOffset(SAO_PAULO_OFFSET, true).format('DD/MM/YYYY HH:mm');
}

export function formatProgrammingDateTimeCompact(value?: string | Date | null): string {
    if (!value) return '—';
    return moment.parseZone(value).utcOffset(SAO_PAULO_OFFSET, true).format('DD/MM HH:mm');
}

/** Actual* (GPS): instante UTC real → exibição America/Sao_Paulo. */
export function formatActualUtcAsSaoPaulo(value?: string | Date | null): string {
    if (!value) return '—';
    const instant = value instanceof Date
        ? moment.utc(value)
        : moment.parseZone(String(value)).utc();
    if (!instant.isValid()) return '—';
    return instant.utcOffset(SAO_PAULO_UTC_OFFSET_MINUTES).format('DD/MM/YYYY HH:mm');
}

export function formatActualUtcAsSaoPauloCompact(value?: string | Date | null): string {
    if (!value) return '—';
    const instant = value instanceof Date
        ? moment.utc(value)
        : moment.parseZone(String(value)).utc();
    if (!instant.isValid()) return '—';
    return instant.utcOffset(SAO_PAULO_UTC_OFFSET_MINUTES).format('DD/MM HH:mm');
}

function truncateText(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}…`;
}

export function formatProgrammingRoute(programming: {
    originReferenceName?: string;
    destinationReferenceName?: string;
    returnDestinationReferenceName?: string;
}) {
    const origin = programming.originReferenceName || '—';
    const destination = programming.destinationReferenceName || '—';
    const returnRef = programming.returnDestinationReferenceName || origin;
    const full = `${origin} → ${destination} → ${returnRef}`;
    const short = `${truncateText(origin, 14)} → ${truncateText(destination, 14)}`;
    return { full, short };
}

export function toDatetimeLocalValue(value?: string | null): string | undefined {
    if (!value) return undefined;
    return moment.parseZone(value).utcOffset(SAO_PAULO_OFFSET, true).format('YYYY-MM-DDTHH:mm');
}

export function resolveIsGoAndReturn(programming: ProgrammingStageTimestamps): boolean {
    return programming.isGoAndReturn ?? programming.isReturn ?? false;
}

export function getProgrammingTimelineStages(programming: ProgrammingStageTimestamps): TimelineStage[] {
    const isGoAndReturn = resolveIsGoAndReturn(programming);

    const stages: TimelineStage[] = [
        {
            key: 'origin-departure',
            label: 'Saída origem',
            expected: programming.expectedOriginDepartureAt ?? programming.expectedDepartureTime,
            actual: programming.actualOriginExitedAt,
        },
        {
            key: 'destination-arrival',
            label: 'Chegada destino',
            expected: programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime,
            actual: programming.actualDestinationEnteredAt,
        },
        {
            key: 'destination-departure',
            label: 'Saída destino',
            expected: programming.expectedDestinationDepartureAt,
            actual: programming.actualDestinationExitedAt,
        },
    ];

    if (isGoAndReturn) {
        stages.push({
            key: 'return-arrival',
            label: 'Chegada retorno',
            expected: programming.expectedReturnArrivalAt,
            actual: programming.actualReturnEnteredAt,
        });
    }

    return stages;
}

export function getCompactStageColumns(
    programming: ProgrammingStageTimestamps,
    options?: { compact?: boolean }
) {
    const formatPlanned = options?.compact ? formatProgrammingDateTimeCompact : formatProgrammingDateTime;
    const formatActual = options?.compact ? formatActualUtcAsSaoPauloCompact : formatActualUtcAsSaoPaulo;

    return {
        expectedOriginDeparture: formatPlanned(
            programming.expectedOriginDepartureAt ?? programming.expectedDepartureTime
        ),
        expectedDestinationArrival: formatPlanned(
            programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime
        ),
        actualOriginDeparture: formatActual(programming.actualOriginExitedAt),
        actualDestinationArrival: formatActual(programming.actualDestinationEnteredAt),
    };
}

function expectedWallClockMoment(value: string): moment.Moment {
    return moment.parseZone(value).utcOffset(SAO_PAULO_OFFSET, true);
}

function utcInstantToWallClockMoment(instant: moment.Moment): moment.Moment | null {
    if (!instant.isValid()) return null;
    const wall = instant.utcOffset(SAO_PAULO_UTC_OFFSET_MINUTES).format('YYYY-MM-DDTHH:mm:ss');
    return moment.parseZone(`${wall}Z`).utcOffset(SAO_PAULO_OFFSET, true);
}

function actualUtcToWallClockMoment(value: string | Date): moment.Moment | null {
    const instant = value instanceof Date
        ? moment.utc(value)
        : moment.parseZone(String(value)).utc();
    return utcInstantToWallClockMoment(instant);
}

/** Relógio de parede SP “agora”, mesma base que planejado legacy. */
export function nowWallClockMoment(nowDate: Date = new Date()): moment.Moment {
    return utcInstantToWallClockMoment(moment.utc(nowDate))!;
}

/** Actual* (UTC) − Expected* (parede SP), em minutos (arredondado). */
export function plannedVsActualDeltaMinutes(
    planned?: string | null,
    actual?: string | null
): number | null {
    if (!planned || !actual) return null;
    const p = expectedWallClockMoment(planned);
    const a = actualUtcToWallClockMoment(actual);
    if (!p.isValid() || !a) return null;
    return Math.round(a.diff(p, 'minutes', true));
}

/** Agora (parede SP) − Expected* (parede SP), em minutos. Positivo = planejado já passou. */
export function plannedVsNowDeltaMinutes(
    planned?: string | null,
    nowDate: Date = new Date()
): number | null {
    if (!planned) return null;
    const p = expectedWallClockMoment(planned);
    const n = nowWallClockMoment(nowDate);
    if (!p.isValid()) return null;
    return Math.round(n.diff(p, 'minutes', true));
}
